import { useEffect, useMemo, useRef, useState } from "react";
import { FiMenu, FiSearch } from "react-icons/fi";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Spinner, Table } from "reactstrap";
import { searchDistributorProducts } from "../../api/edi.api";
import { routes } from "../../routes";

import { useDispatch } from "react-redux";
import ProfileDropdown from "./ProfileDropdown";
import { setThemeColor } from "../../store/actions/actions";

const items = [
  {
    key: "1",
    label: "Profile",
    href: "/admin/user-profile-page",
    show: ["admin", "user"],
  },
  { key: "2", label: "User Management Settings", href: "/admin/settings", show: ["admin"], permissionName: "Settings" },
  { key: "3", label: "System Settings", href: "/admin/system-settings", show: ["admin"], permissionName: "Settings" },
  { key: "4", label: "Categories", href: "/admin/categories", show: ["admin"] },
  {
    key: "5",
    label: "Sub Categories",
    href: "/admin/sub-categories",
    show: ["admin"],
  },
  {
    key: "5_1",
    label: "Product Variant/Groups",
    href: "/admin/product-groups",
    show: ["admin"],
  },
  {
    key: "6",
    label: "Allowed Manufacturers",
    href: "/admin/allowed-manufacturers",
    show: ["admin"],
  },
  {
    key: "7",
    label: "DNH Jobs",
    href: "/admin/dnh-jobs",
    show: ["admin"],
  },
  { key: "8", label: "Tracking", href: "/admin/shipping-tracking", show: ["admin"] },
  { key: "9", label: "Reviews", href: "/admin/reviews", show: ["admin", "user"] },
];


const AdminTopbar = ({ collapsed, setCollapsed }) => {

  const { all_products } = useSelector((state) => state.products);
  const { layout, themeColor } = useSelector((state) => state.redux);
  const dispatch = useDispatch();
  const searchRef = useRef(null);

  const { pathname } = useLocation();

  const [products, setProducts] = useState(all_products ?? []);
  const [duplicateProducts, setDuplicateProducts] = useState([]);
  const [dropdown, setDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [distributorResults, setDistributorResults] = useState([]);
  const [distributorsList, setDistributorsList] = useState([]);
  const [manufacturerDistributors, setManufacturerDistributors] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const isDebouncing = search !== debouncedSearch && search.trim().length > 0;
  const isLoading = isDebouncing || searchLoading;

  // Local search
  const filteredProducts = useMemo(() => {
    let text = debouncedSearch.trim().toUpperCase();
    return duplicateProducts.filter((item) => {
      const searchString = `${item?.id} ${item?.status} ${item?.description} ${item?.price} ${item?.name} ${item?.sku} ${item?.mfg_name} ${item?.vendorName}`;
      return searchString.toUpperCase().includes(text);
    });
  }, [debouncedSearch, duplicateProducts]);

  const groupedProducts = useMemo(() => {
    const groupByMfg = (products) => {
      const grouped = products.reduce((acc, product) => {
        const { mfg_name, stc_quantity } = product;
        const quantity = Number(stc_quantity) || 0;
        if (!acc[mfg_name]) {
          acc[mfg_name] = { mfg_name, totalQuantity: 0, products: [] };
        }
        acc[mfg_name].totalQuantity += quantity;
        acc[mfg_name].products.push(product);
        return acc;
      }, {});
      return Object.values(grouped);
    };
    return groupByMfg(filteredProducts);
  }, [filteredProducts]);

  // Combined Results
  const combinedResults = useMemo(() => {
    const distributorMapped = Array.isArray(distributorResults)
      ? distributorResults.map((d) => ({
        vendor: d.distributor,
        part: d.partNumber,
        stock: d.quantity,
        price: d.price,
      }))
      : [];
    const internalMapped = Array.isArray(groupedProducts)
      ? groupedProducts.map((group) => ({
        vendor: "Techmicro",
        part: group?.products?.[0]?.sku ? group.products[0].sku.slice(4) : "N/A",
        stock: group?.totalQuantity ?? 0,
        price: group?.products?.[0]?.price ?? "N/A",
      }))
      : [];
    return [...internalMapped, ...distributorMapped];
  }, [groupedProducts, distributorResults]);

  // Initial products
  useEffect(() => {
    const filtered = all_products?.filter((i) => i?.stc_quantity > 0) ?? [];
    setProducts(filtered);
    setDuplicateProducts(filtered);
  }, [all_products]);

  // API Call
  useEffect(() => {
    if (debouncedSearch) {
      setSearchLoading(true);
      searchDistributorProducts(debouncedSearch, (result) => {
        if (result?.status === 200) {
          setDistributorResults(result.data?.data ?? []);
          setDistributorsList(result.data?.distributors ?? []);
          setManufacturerDistributors(result.data?.manufacturerDistributors ?? []);
        } else {
          setDistributorResults([]);
          setDistributorsList([]);
          setManufacturerDistributors([]);
        }
        setSearchLoading(false);
      });
    } else {
      setDistributorResults([]);
      setDistributorsList([]);
      setManufacturerDistributors([]);
      setSearchLoading(false);
    }
  }, [debouncedSearch]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearch("");
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get current page title
  const currentRoute = routes.find((route) => pathname.includes(route.path));
  const pageTitle = currentRoute ? currentRoute.name : "Dashboard";

  return (
    <div className="admin-topbar">
      <div className="d-flex align-items-center gap-3">
        <button
          className="btn btn-light d-md-none p-1 border-0"
          onClick={() => setCollapsed(!collapsed)}
        >
          <FiMenu size={20} />
        </button>
        <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: "18px" }}>{pageTitle}</h4>
      </div>




      <div className="topbar-search-wrapper" ref={searchRef}>
        <FiSearch className="topbar-search-icon" size={16} />
        <input
          type="text"
          className="topbar-search"
          placeholder="Search Stock..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setDropdown(!!e.target.value);
          }}
        />

        {dropdown && (
          <div className="topbar-search-dropdown p-2">
            {isLoading ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-4">
                <Spinner color="primary" size="sm" />
                <span className="text-muted small mt-2">Searching distributors...</span>
              </div>
            ) : (
              <>
                <Table size="sm" borderless hover responsive className="mb-0" style={{ fontSize: "13px" }}>
                  <thead className="bg-light text-secondary">
                    <tr>
                      <th className="fw-semibold">Vendor</th>
                      <th className="fw-semibold">Part</th>
                      <th className="fw-semibold">Stock</th>
                      <th className="fw-semibold">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedResults?.length > 0 ? (
                      combinedResults.map((row, idx) => (
                        <tr key={idx} className="border-bottom">
                          <td className="fw-medium text-dark">{row.vendor}</td>
                          <td>{row.part}</td>
                          <td>
                            <span className={`badge ${row.stock > 0 ? 'bg-success' : 'bg-danger'}`}>
                              {row.stock}
                            </span>
                          </td>
                          <td className="fw-bold text-success">${row.price}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-3 text-muted">
                          No results found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                {distributorsList?.length > 0 && (
                  <div className="mt-3 pt-2 border-top">
                    <span className="text-muted d-block mb-2" style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase" }}>
                      Found in Distributors
                    </span>
                    <div className="d-flex flex-wrap gap-1">
                      {distributorsList.map((dist, idx) => (
                        <span
                          key={idx}
                          className="badge bg-light text-dark border px-2 py-1"
                          style={{ fontSize: "11px", fontWeight: "500" }}
                        >
                          {dist}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {manufacturerDistributors?.length > 0 && (
                  <div className="mt-3 pt-2 border-top">
                    <span className="text-muted d-block mb-2" style={{ fontSize: "11px", fontWeight: "600", textTransform: "uppercase" }}>
                      Manufacturer Distributors
                    </span>
                    <div className="d-flex flex-wrap gap-1">
                      {manufacturerDistributors.map((dist, idx) => (
                        <span
                          key={idx}
                          className="badge bg-light text-danger border px-2 py-1"
                          style={{ fontSize: "11px", fontWeight: "500" }}
                        >
                          {dist}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="d-flex align-items-center gap-3 ms-3">
        <ProfileDropdown
          items={items}
          isOpen={profileDropdownOpen}
          toggleDropdown={() => setProfileDropdownOpen(!profileDropdownOpen)}
        />
      </div>


    </div>
  );
};

export default AdminTopbar;

